/* /app/frontend/src/components/dealroom/IssueMemorandumOfSaleModal.jsx
 *
 * iter-398 · P0 · MoS workflow modal.
 * iter-398B · P0 · Modal-hardening + progression fix.
 *
 * Behaviour contract
 * ------------------
 *   * Portal-mounted on `document.body` so the modal escapes any
 *     transformed / overflow-clipping ancestor.
 *   * Fixed viewport-centred layout with sticky header + footer;
 *     the middle scrolls when content overflows.
 *   * Background scroll locked while open.
 *   * Escape key, X button, and click-outside all dismiss.
 *   * Both send + already-sent paths await the mailbox POST, the
 *     journey `memo/complete` POST, AND the parent's SSOT refresh
 *     BEFORE the success toast fires — so the toast can never
 *     precede the persisted state.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X, Send, CheckCircle2, Loader2, Download, FileText, Mail, AlertTriangle,
  ChevronDown, ChevronUp, PoundSterling, Calendar, Users, Landmark,
  Home as HomeIcon, ArrowUpRight, Pencil,
} from "lucide-react";
import { api, apiError } from "../../lib/api";
import { openEditProperty } from "../../data/chainStore";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";


/* ─── UK date input helper ─────────────────────────────────────────
 * Chromium's native `<input type="date">` renders the placeholder
 * (and formats keyboard entry) using the browser's active locale.
 * On the preview infra that resolves to US mm/dd/yyyy — misleading
 * for the UK pilot cohort.  `UkDateInput` renders a masked text
 * input that always reads dd/mm/yyyy in the field while storing
 * the underlying value as an ISO `YYYY-MM-DD` string so every
 * downstream consumer (PATCH payload, `<input type=date>` mirror
 * for pickers, cascade math) stays on the canonical wire format.
 *
 * • Displays `dd/mm/yyyy` at all times.
 * • Auto-inserts `/` separators after day + month.
 * • Emits an ISO string (`YYYY-MM-DD`) via onChange when the field
 *   parses cleanly; emits `""` otherwise.
 * • Preserves every existing className + testid contract of the
 *   underlying `<input>` it replaces.
 */
function isoToUk(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}
function ukToIso(uk) {
  if (!uk || typeof uk !== "string") return "";
  const m = uk.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  const day = parseInt(m[1], 10);
  const mon = parseInt(m[2], 10);
  const yr  = parseInt(m[3], 10);
  if (!yr || mon < 1 || mon > 12 || day < 1 || day > 31) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}
function UkDateInput({ value, onChange, className, "data-testid": testid, ariaLabel }) {
  const [text, setText] = useState(isoToUk(value));
  // Keep display in sync when the parent resets or hydrates the field.
  useEffect(() => {
    const nextDisplay = isoToUk(value);
    setText((prev) => (ukToIso(prev) === value ? prev : nextDisplay));
  }, [value]);
  const handleChange = (e) => {
    // Strip anything that isn't a digit, then re-insert `/`s.
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let masked = digits;
    if (digits.length > 4) masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(masked);
    const iso = ukToIso(masked);
    // Only emit when we have a full valid date; emit "" on clear.
    if (digits.length === 0) onChange("");
    else if (iso) onChange(iso);
  };
  const handleBlur = () => {
    // On blur, if the value is incomplete drop it so the field
    // doesn't hold onto partial digits between focus swaps.
    const iso = ukToIso(text);
    if (!iso && text) {
      setText("");
      onChange("");
    }
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="dd/mm/yyyy"
      maxLength={10}
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      data-testid={testid}
      data-uk-date="true"
      aria-label={ariaLabel}
    />
  );
}


/* ─── P69 · Transaction Baseline (Step 0) enum sources of truth ── */
const FUNDING_BASIS_OPTIONS = [
  { value: "cash",           label: "Cash",              needsBroker: false },
  { value: "mortgage",       label: "Mortgage",          needsBroker: true  },
  { value: "gift_mortgage",  label: "Gift + Mortgage",   needsBroker: true  },
];
const CHAIN_POSITION_OPTIONS = [
  { value: "top",         label: "Top of chain" },
  { value: "two",         label: "Chain of two" },
  { value: "three_plus",  label: "Chain of three or more" },
];
const REQUIRED_FIELDS = [
  "buyer_id", "buyer_solicitor_id", "seller_solicitor_id",
  "agreed_price", "funding_basis",
];
/* Human-readable labels for the "missing X" banner + Step 0 chips. */
const FIELD_LABEL = {
  buyer_id:            "Buyer",
  buyer_solicitor_id:  "Buyer Solicitor",
  seller_solicitor_id: "Seller Solicitor",
  broker_id:           "Mortgage Broker",
  agreed_price:        "Agreed Price",
  funding_basis:       "Funding Basis",
};


function StepBadge({ n, label, active, done }) {
  return (
    <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${active ? "text-violet-700" : done ? "text-emerald-700" : "text-slate-400"}`}>
      <span
        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
          active ? "bg-violet-600 text-white"
            : done ? "bg-emerald-600 text-white"
              : "bg-slate-200 text-slate-500"
        }`}
      >{done ? "✓" : n}</span>
      {label}
    </div>
  );
}


export default function IssueMemorandumOfSaleModal({ deal, onClose, onCompleted }) {
  // P69 · Step 0 baseline capture is the new default entry phase.
  // Phases now flow: baseline → choose → preview → success.
  const [phase, setPhase] = useState("baseline");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [includePdf, setIncludePdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [markingSent, setMarkingSent] = useState(false);

  // P69 · Transaction Baseline (Step 0) state.
  const [baselineLoading, setBaselineLoading] = useState(true);
  const [baseline, setBaseline] = useState(null);       // last GET snapshot
  const [contacts, setContacts] = useState([]);          // /api/contacts
  const [baselineForm, setBaselineForm] = useState(null); // editable form
  const [baselineExpanded, setBaselineExpanded] = useState(true);
  const [baselineSaving, setBaselineSaving] = useState(false);
  const [baselineError, setBaselineError] = useState(null);

  const dealId = deal?.id;
  const busy = sending || markingSent || baselineSaving;

  // ── Body scroll lock ────────────────────────────────────────
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Escape dismissal ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [busy, onClose]);

  // ── P69 · Baseline fetch (with contacts side-load) ──────────
  //
  // iter-alignment · P0 · MoS Baseline Load Reliability
  // (2026-02-15) — DECOUPLE the two fetches.  A `Promise.all`
  // meant a `/api/contacts` hiccup would wipe an otherwise-
  // healthy baseline load with the same "Couldn't load the
  // baseline" toast.  Now each request lives in its own
  // try/catch:
  //
  //   • Baseline is REQUIRED — its failure blocks Confirm.
  //   • Contacts is OPTIONAL — its failure downgrades to an
  //     empty picker list but never fails Step 0.
  //
  // Failure diagnostics carry a copyable reference id (the
  // backend's `x-request-id` when present, else a client-side
  // correlation id) + the HTTP status the endpoint returned so
  // operators can quote the exact reference to support.  No
  // clipped text — the ref renders in a `break-all` <code>
  // pill with a Copy button.
  useEffect(() => {
    let alive = true;
    setBaseline(null);
    setBaselineForm(null);
    setBaselineError(null);
    if (!dealId) {
      setBaselineLoading(false);
      setBaselineError({ message: "Deal id missing — cannot load baseline.", refId: null, status: null });
      return;
    }
    setBaselineLoading(true);
    (async () => {
      let b = null;
      let baselineFailure = null;
      try {
        const baselineRes = await api.get(`/api/v2/deals/${dealId}/promotion-baseline`);
        if (!alive) return;
        b = baselineRes?.data || {};
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status ?? null;
        const refId =
          e?.response?.headers?.["x-request-id"]
          || e?.response?.headers?.["x-correlation-id"]
          || `MOS-${dealId?.slice?.(0, 8) || "unknown"}-${Date.now().toString(36)}`;
        baselineFailure = {
          message: apiError(e) || "Could not load baseline.",
          refId,
          status,
        };
      }
      // Contacts side-load — never fails the baseline step.
      let cs = [];
      try {
        const contactsRes = await api.get(`/api/contacts`);
        if (!alive) return;
        cs = Array.isArray(contactsRes?.data)
          ? contactsRes.data
          : (contactsRes?.data?.items || []);
      } catch (_e) {
        // Optional — swallow.  Merge branch below tolerates [].
        cs = [];
      }
      if (!alive) return;
      if (baselineFailure) {
        setBaseline(null);
        setBaselineForm(null);
        setBaselineError(baselineFailure);
        setBaselineLoading(false);
        return;
      }
      setBaseline(b);
      // Merge Postgres-side hydrated contact snapshots into the
      // Mongo-picker list so the participant selects can render
      // the actual name even when the id only exists on one
      // side of the Mongo / Postgres store split.
      const merged = [...cs];
      const seenIds = new Set(cs.map((c) => c.id));
      for (const hintField of ["buyer", "buyer_solicitor", "seller_solicitor", "broker"]) {
        const snap = b?.[hintField];
        if (snap && snap.id && !seenIds.has(snap.id)) {
          merged.push({
            id: snap.id,
            name: snap.name || snap.email || "Unnamed contact",
            email: snap.email || "",
            company: snap.company || "",
            role: snap.role || "",
          });
          seenIds.add(snap.id);
        }
      }
      setContacts(merged);
      // iter-alignment · P0 · MoS · Transaction Baseline UX Cleanup
      // (2026-02) — Property-scoped fields (address / postcode /
      // type / tenure) are no longer editable inside the MoS modal;
      // they now render as read-only SSOT rows with an `Edit in
      // Property →` deep link.  Form state omits them entirely so
      // the PATCH diff never re-echoes property columns and the
      // canonical Property editor remains the single source of
      // truth for property attributes.
      setBaselineForm({
        buyer_id:            b.buyer_id            || "",
        buyer_solicitor_id:  b.buyer_solicitor_id  || "",
        seller_solicitor_id: b.seller_solicitor_id || "",
        broker_id:           b.broker_id           || "",
        agreed_price:        b.agreed_price        || "",
        target_exchange:     b.target_exchange     || "",
        target_completion:   b.target_completion   || "",
        funding_basis:       b.funding_basis       || "",
        chain_position:      b.chain_position      || "",
        lender:              b.lender              || "",
        special_conditions:  b.special_conditions  || "",
      });
      setBaselineExpanded(!(b?.completeness?.is_complete));
      setBaselineLoading(false);
    })();
    return () => { alive = false; };
  }, [dealId]);

  // ── Preview fetch ───────────────────────────────────────────
  useEffect(() => {
    if (phase !== "preview" || !dealId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/v2/deals/${dealId}/memorandum-of-sale/preview`);
        if (!alive) return;
        setPreview(data);
        setSubject(data.subject || "");
        setBodyHtml(data.body_html || "");
        setTo((data.default_to || []).map((s) => s.email).join(", "));
        setCc((data.default_cc || []).map((s) => s.email).join(", "));
      } catch (e) {
        toast.error(apiError(e));
        setPhase("choose");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [phase, dealId]);

  // ── Send from ChainFlow ─────────────────────────────────────
  // iter-398B — every side effect (send, journey-complete, parent
  // SSOT refresh) awaited BEFORE the success toast.  Journey advance
  // errors are surfaced so a silent partial-failure can't leave the
  // deal on the wrong stage.
  const sendFromChainFlow = useCallback(async () => {
    if (!to.trim()) { toast.error("At least one recipient is required."); return; }
    setSending(true);
    try {
      await api.post(`/api/v2/deals/${dealId}/memorandum-of-sale/send`, {
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        subject,
        body_html: bodyHtml,
        include_pdf: includePdf,
      });
      // Canonical journey advance — cascade fires stage/status/
      // activity refresh downstream.  Idempotent server-side.
      try {
        await api.post(`/api/v2/deals/${dealId}/journey/memo/complete`);
      } catch (e) {
        // If the memo step is already complete the endpoint may
        // return an error — that is fine here; the important
        // milestone (MoS sent) is already persisted.  Surface the
        // failure only in the console so ops can spot patterns.
        // eslint-disable-next-line no-console
        console.warn("MoS journey advance skipped:", e?.response?.data || e?.message);
      }
      // Wait for the parent Deal Room to refetch deal + activity +
      // Exchange Confidence so the completed summary renders from
      // fresh SSOT before we tell the operator anything succeeded.
      await Promise.resolve(onCompleted?.());
      setPhase("success");
      toast.success("Memorandum of Sale sent · Deal Room refreshed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  }, [dealId, to, cc, subject, bodyHtml, includePdf, onCompleted]);

  // ── Already sent ────────────────────────────────────────────
  // iter-alignment · P74 · Editable sent date + optional memo
  // note travel with the mark-sent payload.  Server persists them
  // on the DealActivity meta as `sent_at` / `memo_filename`.
  const [externalSentDate, setExternalSentDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [externalMemoFile, setExternalMemoFile] = useState(null);
  const markAlreadySent = useCallback(async () => {
    setMarkingSent(true);
    try {
      const payload = {};
      if (externalSentDate) payload.sent_at = externalSentDate;
      if (externalMemoFile?.name) payload.memo_filename = externalMemoFile.name;
      await api.post(`/api/v2/deals/${dealId}/memorandum-of-sale/mark-sent`, payload);
      try {
        await api.post(`/api/v2/deals/${dealId}/journey/memo/complete`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("MoS (already-sent) journey advance skipped:", e?.response?.data || e?.message);
      }
      await Promise.resolve(onCompleted?.());
      setPhase("success");
      toast.success("Memorandum marked as already sent · Deal Room refreshed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMarkingSent(false);
    }
  }, [dealId, externalSentDate, externalMemoFile, onCompleted]);

  // ── P69 · Derived baseline signals ──────────────────────────
  // `missingRequired` — hard-required fields not yet satisfied
  // by the CURRENT baselineForm (recomputed on every keystroke
  // so the summary chip and Confirm button react in-place).
  const missingRequired = useMemo(() => {
    if (!baselineForm) return REQUIRED_FIELDS.slice();
    const brokerRequired = FUNDING_BASIS_OPTIONS.find(
      (o) => o.value === baselineForm.funding_basis
    )?.needsBroker;
    const check = (field) => {
      const v = baselineForm[field];
      return v == null || String(v).trim() === "";
    };
    const missing = REQUIRED_FIELDS.filter(check);
    if (brokerRequired && check("broker_id")) missing.push("broker_id");
    return missing;
  }, [baselineForm]);
  const baselineComplete = missingRequired.length === 0;

  // ── P69 · Confirm & continue ────────────────────────────────
  const confirmBaseline = useCallback(async () => {
    if (!baselineForm || !dealId) return;
    setBaselineSaving(true);
    setBaselineError(null);
    try {
      // Only send fields that changed vs the last GET snapshot to
      // keep the PATCH surgical (avoids re-writing unchanged values
      // and keeps the audit log clean).
      const patch = {};
      const HINT_FIELDS = {
        buyer_id:            "buyer",
        buyer_solicitor_id:  "buyer_solicitor",
        seller_solicitor_id: "seller_solicitor",
        broker_id:           "broker",
      };
      // iter-alignment · P0 · MoS · Transaction Baseline UX Cleanup
      // (2026-02) — Property-scoped fields were retired from the
      // baselineForm state so the diff logic only needs the flat
      // top-level lookup.  Property edits now flow through the
      // canonical Property editor via `openEditProperty`.
      for (const k of Object.keys(baselineForm)) {
        const raw = baselineForm[k];
        const cur = baseline?.[k] ?? null;
        const norm = raw === "" ? null : raw;
        if (norm !== cur) patch[k] = norm;
        // For participant FKs, ALSO send a `ContactHint` object so
        // the server can resolve Mongo→Postgres contact ids when
        // the two stores drift (see routes_deal_promotion._resolve_contact).
        if (HINT_FIELDS[k] && norm) {
          const contact = contacts.find((c) => c.id === norm);
          if (contact) {
            patch[HINT_FIELDS[k]] = {
              id:      contact.id,
              name:    contact.name || null,
              email:   contact.email || null,
              phone:   contact.phone || null,
              company: contact.company || null,
              role:    contact.role || null,
            };
          }
        }
      }
      if (Object.keys(patch).length > 0) {
        const { data } = await api.patch(
          `/api/v2/deals/${dealId}/promotion-baseline`,
          patch,
        );
        setBaseline(data);
      }
      setPhase("choose");
    } catch (e) {
      setBaselineError(apiError(e));
      toast.error(apiError(e));
    } finally {
      setBaselineSaving(false);
    }
  }, [dealId, baselineForm, baseline, contacts]);

  // ── P69 · Lightweight setter used by every Step 0 input ─────
  const setBaselineField = useCallback((field, value) => {
    setBaselineForm((f) => (f ? { ...f, [field]: value } : f));
  }, []);

  if (!deal) return null;
  const node = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/45 p-4"
      data-testid="mos-modal-overlay"
      onClick={busy ? undefined : onClose}
      style={{ overscrollBehavior: "contain" }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Issue Memorandum of Sale"
        data-testid="mos-modal"
      >
        {/* Sticky header */}
        <header className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-white">
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-slate-900 leading-tight">Issue Memorandum of Sale</h3>
            {/* iter-alignment · P0 · MoS · Transaction Baseline UX Cleanup
             * (2026-02) — Header now reads the canonical full address +
             * postcode straight off the baseline SSOT snapshot.  Falls
             * back to `deal.property_address` during the first paint
             * (before the baseline GET resolves) and finally to the deal
             * name so the sub-line never renders empty. */}
            <p className="mt-0.5 text-[11.5px] text-slate-500 truncate" data-testid="mos-modal-header-property">
              {(() => {
                const addr = baseline?.property?.address || deal.property_address || deal.name || "";
                const pc = baseline?.property?.postcode || "";
                return pc ? `${addr} · ${pc}` : addr;
              })()}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="mos-modal-close" aria-label="Close">
            <X size={16} strokeWidth={2.4} />
          </button>
        </header>

        <div className="shrink-0 px-5 py-3 flex items-center gap-3 border-b border-slate-100 bg-slate-50/50">
          <StepBadge n={1} label="Transaction Baseline" active={phase === "baseline"} done={phase !== "baseline"} />
          <span className="w-4 h-px bg-slate-200" />
          <StepBadge n={2} label="Choose path" active={phase === "choose"} done={phase === "preview" || phase === "success"} />
          <span className="w-4 h-px bg-slate-200" />
          <StepBadge n={3} label="Review & send" active={phase === "preview" || phase === "sending"} done={phase === "success"} />
          <span className="w-4 h-px bg-slate-200" />
          <StepBadge n={4} label="Done" active={phase === "success"} done={phase === "success"} />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {phase === "baseline" && (
            <BaselineStep
              loading={baselineLoading}
              error={baselineError}
              baseline={baseline}
              form={baselineForm}
              contacts={contacts}
              missingRequired={missingRequired}
              expanded={baselineExpanded}
              onToggleExpanded={() => setBaselineExpanded((v) => !v)}
              onField={setBaselineField}
              onEditPropertyDeepLink={() => {
                /* iter-alignment · P0 · MoS · Transaction Baseline UX
                 * Cleanup (2026-02) — close the MoS modal and hand off
                 * to the canonical Edit Property drawer.  Property
                 * attributes (address / postcode / type / tenure) live
                 * ONLY on the Property SSOT — no duplicate state, no
                 * inline property inputs. */
                const snap = baseline?.property;
                if (snap && snap.id) {
                  openEditProperty({
                    id:       snap.id,
                    address:  snap.address,
                    postcode: snap.postcode,
                    type:     snap.property_type,
                    tenure:   snap.tenure,
                  });
                  onClose?.();
                } else {
                  toast.error("This deal has no linked property yet — open it from the Properties page first.");
                }
              }}
            />
          )}

          {phase === "choose" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mos-choose">
              <button
                type="button"
                onClick={() => setPhase("preview")}
                disabled={busy}
                className="text-left rounded-xl ring-1 ring-slate-200 hover:ring-violet-400 hover:bg-violet-50/40 p-4 transition disabled:opacity-60"
                data-testid="mos-choose-send"
              >
                <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center mb-3">
                  <Send size={14} strokeWidth={2.4} />
                </div>
                <h4 className="text-[13.5px] font-bold text-slate-900">Send from ChainFlow</h4>
                <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">
                  Generate the Memorandum, pick recipients, review, and send via your connected mailbox.  PDF attachment included by default.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setPhase("external")}
                disabled={busy}
                className="text-left rounded-xl ring-1 ring-slate-200 hover:ring-emerald-400 hover:bg-emerald-50/40 p-4 transition disabled:opacity-60"
                data-testid="mos-choose-already-sent"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                  <CheckCircle2 size={14} strokeWidth={2.4} />
                </div>
                <h4 className="text-[13.5px] font-bold text-slate-900">Already sent / send externally</h4>
                <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">
                  Confirm the Memorandum was issued outside ChainFlow.  Set the sent date and optionally attach the memo you sent.
                </p>
              </button>
            </div>
          )}

          {/* iter-alignment · P74 · External send confirmation.
              Editable sent date (defaults to today) + optional
              memo upload; Mark as issued POSTs to the existing
              mark-sent endpoint which persists both fields on the
              DealActivity meta (SSOT). */}
          {phase === "external" && (
            <div className="space-y-4" data-testid="mos-external">
              <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2.5">
                <p className="text-[12.5px] text-emerald-800 font-medium">
                  Confirm that the Memorandum of Sale was already sent through another channel.  Both paths complete the same journey milestone.
                </p>
              </div>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Sent date</span>
                <UkDateInput
                  value={externalSentDate}
                  onChange={setExternalSentDate}
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  data-testid="mos-external-sent-date"
                  ariaLabel="Sent date"
                />
                <span className="mt-1 block text-[10.5px] text-slate-400">
                  Defaults to today — edit if the memo was sent earlier.
                </span>
              </label>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Attach memo (optional)</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.eml,.msg"
                  onChange={(e) => setExternalMemoFile(e.target.files?.[0] || null)}
                  className="block w-full text-[12.5px] text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[12px] file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  data-testid="mos-external-memo-file"
                />
                {externalMemoFile && (
                  <span
                    className="mt-1 block text-[11.5px] text-slate-600 truncate"
                    data-testid="mos-external-memo-selected"
                  >
                    Selected: {externalMemoFile.name}
                  </span>
                )}
              </label>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setPhase("choose")}
                  disabled={markingSent}
                  className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                  data-testid="mos-external-back"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={markAlreadySent}
                  disabled={markingSent || !externalSentDate}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="mos-external-mark-issued"
                >
                  {markingSent ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} strokeWidth={2.4} />}
                  Mark as issued
                </button>
              </div>
            </div>
          )}

          {phase === "preview" && loading && (
            <div className="flex items-center gap-2 text-[13px] text-slate-500" data-testid="mos-preview-loading">
              <Loader2 size={13} className="animate-spin" /> Loading Memorandum draft…
            </div>
          )}

          {phase === "preview" && !loading && preview && (
            <div className="space-y-4" data-testid="mos-preview">
              {!preview.mailbox_connected && (
                <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 flex items-start gap-2" data-testid="mos-no-mailbox">
                  <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0" strokeWidth={2.4} />
                  <p className="text-[12px] text-amber-800">
                    Gmail isn't connected — the send button will fail.  Head to <b>Settings → Email</b> to connect first, or use <b>Already Sent</b>.
                  </p>
                </div>
              )}
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">To</span>
                <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient email(s), comma-separated" className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300" data-testid="mos-to" />
              </label>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Cc</span>
                <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Optional" className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300" data-testid="mos-cc" />
              </label>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Subject</span>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300" data-testid="mos-subject" />
              </label>
              <div className="rounded-lg ring-1 ring-slate-200 overflow-hidden" data-testid="mos-body-preview">
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                  <span className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 flex items-center gap-1">
                    <FileText size={11} strokeWidth={2.4} /> Preview
                  </span>
                  <a href={`${BACKEND}/api/v2/deals/${dealId}/memorandum-of-sale/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:underline" data-testid="mos-download-pdf">
                    <Download size={11} strokeWidth={2.4} /> Download PDF
                  </a>
                </div>
                <div className="p-3 max-h-72 overflow-auto bg-white text-[13px]" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              </div>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={includePdf} onChange={(e) => setIncludePdf(e.target.checked)} className="rounded border-slate-300 text-violet-600 focus:ring-violet-300" data-testid="mos-include-pdf" />
                Attach PDF copy
              </label>
            </div>
          )}

          {phase === "success" && (
            <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-6 text-center" data-testid="mos-success">
              <CheckCircle2 size={24} className="mx-auto text-emerald-600 mb-2" strokeWidth={2} />
              <p className="text-[13.5px] font-semibold text-slate-900">Memorandum of Sale complete</p>
              <p className="mt-1 text-[12px] text-slate-600">Timeline updated · journey advanced · Exchange Confidence recalculated.</p>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        {(phase === "baseline" || phase === "preview" || phase === "success") && (
          <footer className="shrink-0 px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
            {phase === "baseline" ? (
              <>
                <div
                  className={`text-[11.5px] text-slate-600 min-w-0 flex-1 ${baselineError ? "whitespace-normal break-words" : "truncate"}`}
                  data-testid="mos-baseline-summary"
                >
                  {/* P3 · Baseline Load Fix (2026-02-28) — the
                      summary switch reads `baselineError` FIRST
                      so success ("Baseline verified") and error
                      states are mutually exclusive by
                      construction.  Loading always wins over
                      any pre-existing state so the operator sees
                      progress during a retry.
                      iter-alignment · P0 · MoS Baseline Failure
                      Reopen (2026-08-15) — the error branch now
                      drops `truncate` (via the ternary above) so
                      the message wraps + the HTTP status pill
                      renders inline as designed.  No more
                      clipped diagnostics in the footer. */}
                  {baselineLoading
                    ? "Loading transaction baseline…"
                    : baselineError
                      ? (
                          <span className="inline-flex flex-wrap items-center gap-1 text-rose-700 font-semibold" data-testid="mos-baseline-summary-error">
                            <AlertTriangle size={12} strokeWidth={2.4} />
                            <span className="break-words">{typeof baselineError === "string" ? baselineError : (baselineError?.message || "Could not load baseline.")}</span>
                            {typeof baselineError === "object" && baselineError?.status != null ? (
                              <span className="text-[10.5px] font-semibold text-rose-700/80" data-testid="mos-baseline-summary-error-status">HTTP {baselineError.status}</span>
                            ) : null}
                          </span>
                        )
                      : baselineComplete
                        ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 size={12} strokeWidth={2.4} /> Baseline verified — ready to continue.</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><AlertTriangle size={12} strokeWidth={2.4} /> Missing {missingRequired.length} field{missingRequired.length === 1 ? "" : "s"} — please confirm to proceed.</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50" data-testid="mos-baseline-cancel">Cancel</button>
                  <button
                    type="button"
                    onClick={confirmBaseline}
                    disabled={baselineSaving || baselineLoading || !!baselineError || !baselineComplete}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="mos-baseline-confirm"
                  >
                    {baselineSaving ? <Loader2 size={12} className="animate-spin" /> : null}
                    {baselineSaving ? "Confirming…" : "Confirm & continue"}
                  </button>
                </div>
              </>
            ) : phase === "preview" ? (
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => setPhase("choose")} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50" data-testid="mos-back">Back</button>
                <button
                  type="button"
                  onClick={sendFromChainFlow}
                  disabled={sending || !to.trim() || !preview?.mailbox_connected}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                  data-testid="mos-send"
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} strokeWidth={2.4} />}
                  {sending ? "Sending…" : "Send Memorandum"}
                </button>
              </div>
            ) : (
              <button type="button" onClick={onClose} className="ml-auto px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700" data-testid="mos-done">Done</button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
  return createPortal(node, document.body);
}


/* ─── P69 · Transaction Baseline Step (Step 0) ───────────────────
 * Renders the pre-MoS baseline capture pane.  Two visual modes:
 *   • Compact "Baseline verified" summary when every hard-required
 *     field is already present in SSOT — the operator can advance
 *     with one click.  A calm expand affordance is offered for
 *     review / edits.
 *   • Expanded form otherwise — missing required fields render
 *     with amber "Needs confirmation" chips, soft fields with
 *     calm labels.  Broker becomes hard-required when the operator
 *     picks Mortgage / Gift + Mortgage.
 * All state lives on the parent modal so a Confirm & continue
 * click can PATCH once and hand off to Step 1 unchanged.
 */
function BaselineStep({
  loading, error, baseline, form, contacts,
  missingRequired, expanded, onToggleExpanded, onField,
  onEditPropertyDeepLink,
}) {
  /* iter-alignment · P0 · MoS · Transaction Baseline UX Cleanup
   * (2026-02) — Local edit toggles so the pane behaves as a
   * verification step by default: SSOT values render as calm
   * summary chips and only the field the operator taps enters
   * edit mode.  Property attributes are read-only here — the
   * Edit in Property → link hands off to the canonical Property
   * editor via `openEditProperty` (wired by the parent modal). */
  const [editingFields, setEditingFields] = useState(() => new Set());
  const [showAdditional, setShowAdditional] = useState(false);
  const isEditing   = (key) => editingFields.has(key);
  const openEditFor = (key) => setEditingFields((prev) => {
    const next = new Set(prev); next.add(key); return next;
  });
  /* P3 · Baseline Load Fix (2026-02-28) — the error early-return
   * MUST be evaluated BEFORE the loading gate.  The useEffect's
   * failure path deliberately wipes `form` to null (so the
   * "Baseline verified" chip can never leak beside an error);
   * with the previous ordering (`loading || !form` first) the
   * `!form` check hijacked the render and left the modal body
   * stuck on the loading spinner.  Runtime state on failure is
   *   loading=false · form=null · error=<message>
   * so the error panel now renders correctly. */
  if (error) {
    // iter-alignment · P0 · MoS Baseline Load Reliability
    // (2026-02-15) — error is now either a bare string
    // (legacy call sites) OR a structured `{message, refId,
    // status}` bag.  Both branches render with the message on
    // its own line + a copyable reference id pill (no
    // truncation, `break-all` so long ids never clip).
    const bag = typeof error === "string" ? { message: error, refId: null, status: null } : (error || {});
    const copyRef = () => {
      try { navigator.clipboard?.writeText?.(String(bag.refId || "")); } catch (_) { /* clipboard unavailable */ }
    };
    return (
      <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 px-3 py-2.5 text-[12.5px] text-rose-800" data-testid="mos-baseline-error">
        <p className="font-semibold">Couldn&apos;t load the baseline.</p>
        <p className="mt-0.5 text-[11.5px] text-rose-700/90 whitespace-normal break-words">{bag.message}</p>
        {bag.refId ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[10.5px] uppercase tracking-wide text-rose-700/80 font-semibold">Reference</span>
            <code
              className="inline-block max-w-full px-1.5 py-0.5 rounded bg-white ring-1 ring-rose-200 text-[11px] font-mono text-rose-900 break-all whitespace-normal"
              data-testid="mos-baseline-error-ref"
            >{bag.refId}</code>
            <button
              type="button"
              onClick={copyRef}
              className="text-[11px] font-semibold text-rose-700 hover:text-rose-900 underline underline-offset-2"
              data-testid="mos-baseline-error-copy-ref"
            >Copy</button>
            {bag.status != null ? (
              <span className="text-[10.5px] text-rose-700/80" data-testid="mos-baseline-error-status">HTTP {bag.status}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
  if (loading || !form) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-slate-500" data-testid="mos-baseline-loading">
        <Loader2 size={13} className="animate-spin" /> Loading transaction baseline…
      </div>
    );
  }

  const isVerified = missingRequired.length === 0;
  const brokerRequired = FUNDING_BASIS_OPTIONS.find(
    (o) => o.value === form.funding_basis,
  )?.needsBroker;
  const isCash = form.funding_basis === "cash";

  const contactsByRole = (role) => contacts.filter((c) => c.role === role);
  const findContact    = (id) => contacts.find((c) => c.id === id) || null;

  // Auto-collapsed summary variant — well-prepped deals only.
  // iter-alignment · P0 · MoS · Transaction Baseline UX Cleanup
  // (2026-02) — the collapsed chip now reads "Transaction details
  // confirmed" so the operator sees a verification outcome, not a
  // pipeline internal ("Baseline verified" preserved below inside
  // the expanded pane so the P3 load-fix and P69 promotion locks
  // remain intact).
  if (isVerified && !expanded) {
    return (
      <div className="rounded-xl bg-emerald-50/60 ring-1 ring-emerald-200/70 p-4" data-testid="mos-baseline-summary-collapsed">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-800" data-testid="mos-baseline-confirmed-chip">
              <CheckCircle2 size={13} strokeWidth={2.4} /> Transaction details confirmed
            </p>
            <p className="mt-1 text-[12px] text-slate-700 leading-relaxed">
              {[
                findContact(form.buyer_id)?.name || "Buyer",
                form.agreed_price ? String(form.agreed_price) : null,
                form.funding_basis ? (FUNDING_BASIS_OPTIONS.find((o) => o.value === form.funding_basis)?.label || null) : null,
                form.chain_position ? (CHAIN_POSITION_OPTIONS.find((o) => o.value === form.chain_position)?.label || null) : null,
              ].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button
            type="button"
            onClick={onToggleExpanded}
            className="text-[11.5px] font-semibold text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1"
            data-testid="mos-baseline-expand"
          >
            <ChevronDown size={12} strokeWidth={2.4} /> Review or edit
          </button>
        </div>
      </div>
    );
  }

  const isMissing = (field) => missingRequired.includes(field);

  return (
    <div className="space-y-4" data-testid="mos-baseline-form">
      {/* Header row: eyebrow + collapse-when-verified affordance */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500">Transaction Baseline</p>
          <h4 className="text-[13.5px] font-bold text-slate-900 mt-0.5">
            Confirm the transaction before issuing the Memorandum
          </h4>
          <p className="mt-1 text-[11.5px] text-slate-600 leading-relaxed max-w-lg">
            Pre-filled from your existing records.  Only missing information needs your attention — this should take under a minute for a well-prepped deal.
          </p>
        </div>
        {isVerified ? (
          <button
            type="button"
            onClick={onToggleExpanded}
            className="text-[11.5px] font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
            data-testid="mos-baseline-collapse"
          >
            <ChevronUp size={12} strokeWidth={2.4} /> Collapse
          </button>
        ) : null}
      </div>

      {/* Participants — SSOT summary chips with inline Change action.
       * When a participant is already resolved from SSOT the operator
       * sees `Name · Company` + a small Change control that reveals
       * the canonical picker; missing rows show the picker directly.
       * When funding is Cash the mortgage broker row collapses to a
       * calm `N/A · Cash purchase` display. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="mos-baseline-participants">
        <BaselineContactField
          testid="mos-baseline-buyer"
          label="Buyer" required missing={isMissing("buyer_id")}
          value={form.buyer_id} icon={<Users size={12} strokeWidth={2.4} />}
          options={contactsByRole("Buyer")}
          contact={findContact(form.buyer_id)}
          editing={isEditing("buyer_id")}
          onOpenEdit={() => openEditFor("buyer_id")}
          onChange={(v) => onField("buyer_id", v)}
        />
        <BaselineContactField
          testid="mos-baseline-buyer-solicitor"
          label="Buyer Solicitor" required missing={isMissing("buyer_solicitor_id")}
          value={form.buyer_solicitor_id} icon={<Users size={12} strokeWidth={2.4} />}
          options={contactsByRole("Solicitor")}
          contact={findContact(form.buyer_solicitor_id)}
          editing={isEditing("buyer_solicitor_id")}
          onOpenEdit={() => openEditFor("buyer_solicitor_id")}
          onChange={(v) => onField("buyer_solicitor_id", v)}
        />
        <BaselineContactField
          testid="mos-baseline-seller-solicitor"
          label="Seller Solicitor" required missing={isMissing("seller_solicitor_id")}
          value={form.seller_solicitor_id} icon={<Users size={12} strokeWidth={2.4} />}
          options={contactsByRole("Solicitor")}
          contact={findContact(form.seller_solicitor_id)}
          editing={isEditing("seller_solicitor_id")}
          onOpenEdit={() => openEditFor("seller_solicitor_id")}
          onChange={(v) => onField("seller_solicitor_id", v)}
        />
        <BaselineContactField
          testid="mos-baseline-broker"
          label={brokerRequired ? "Mortgage Broker" : "Mortgage Broker (optional)"}
          required={!!brokerRequired}
          missing={isMissing("broker_id")}
          value={form.broker_id} icon={<Users size={12} strokeWidth={2.4} />}
          options={contactsByRole("Mortgage Broker")}
          contact={findContact(form.broker_id)}
          editing={isEditing("broker_id")}
          onOpenEdit={() => openEditFor("broker_id")}
          onChange={(v) => onField("broker_id", v)}
          cashNa={isCash}
        />
      </div>

      {/* Property — Read-only SSOT rows.  Property attributes live on
       * the Property SSOT; edits route through the canonical Edit
       * Property drawer via `onEditPropertyDeepLink`.  The MoS modal
       * no longer holds property_* form state or PATCH surface —
       * verification only. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="mos-baseline-property">
        <BaselineField
          testid="mos-baseline-property-type"
          label="Property Type" icon={<HomeIcon size={12} strokeWidth={2.4} />}
        >
          <div
            className="w-full rounded-md ring-1 ring-slate-200 bg-slate-50/60 px-2.5 py-1.5 text-[13px] text-slate-800"
            data-testid="mos-baseline-property-type-readonly"
          >
            {baseline?.property?.property_type
              ? baseline.property.property_type
              : <span className="text-slate-400 italic">Not recorded</span>}
          </div>
        </BaselineField>
        <BaselineField
          testid="mos-baseline-property-tenure"
          label="Tenure" icon={<HomeIcon size={12} strokeWidth={2.4} />}
        >
          <div
            className="w-full rounded-md ring-1 ring-slate-200 bg-slate-50/60 px-2.5 py-1.5 text-[13px] text-slate-800"
            data-testid="mos-baseline-property-tenure-readonly"
          >
            {baseline?.property?.tenure
              ? baseline.property.tenure
              : <span className="text-slate-400 italic">Not recorded</span>}
          </div>
        </BaselineField>
      </div>
      <div className="-mt-1 flex items-center justify-end">
        <button
          type="button"
          onClick={onEditPropertyDeepLink}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
          data-testid="mos-baseline-property-edit-link"
        >
          Edit in Property <ArrowUpRight size={11} strokeWidth={2.4} />
        </button>
      </div>

      {/* Financials + funding — Agreed Price + Funding Basis.
       * Agreed Price flips between a calm summary chip and its
       * canonical numeric input via the local Edit toggle;
       * Funding Basis stays as its pill picker (the canonical
       * edit control — no separate summary). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="mos-baseline-financials">
        <BaselineField
          testid="mos-baseline-agreed-price"
          label="Agreed Price" required missing={isMissing("agreed_price")}
          icon={<PoundSterling size={12} strokeWidth={2.4} />}
        >
          {(form.agreed_price && !isEditing("agreed_price")) ? (
            <div
              className="flex items-center justify-between gap-2 w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px]"
              data-testid="mos-baseline-agreed-price-confirmed"
            >
              <span className="font-semibold text-slate-800">{form.agreed_price}</span>
              <button
                type="button"
                onClick={() => openEditFor("agreed_price")}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
                data-testid="mos-baseline-agreed-price-edit"
              >
                <Pencil size={10} strokeWidth={2.4} /> Edit
              </button>
            </div>
          ) : (
            <input
              type="text" inputMode="numeric"
              value={form.agreed_price || ""}
              onChange={(e) => onField("agreed_price", e.target.value)}
              placeholder="£725,000"
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="mos-baseline-agreed-price-input"
            />
          )}
        </BaselineField>
        <BaselineField
          testid="mos-baseline-funding-basis"
          label="Funding Basis" required missing={isMissing("funding_basis")}
          icon={<Landmark size={12} strokeWidth={2.4} />}
        >
          <div className="flex flex-wrap gap-1.5">
            {FUNDING_BASIS_OPTIONS.map((opt) => {
              const active = form.funding_basis === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onField("funding_basis", active ? "" : opt.value)}
                  className={`px-2.5 py-1 rounded-md text-[11.5px] font-semibold ring-1 transition ${active ? "bg-violet-600 text-white ring-violet-600" : "bg-white text-slate-700 ring-slate-200 hover:ring-violet-300"}`}
                  data-testid={`mos-baseline-funding-${opt.value}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </BaselineField>
      </div>

      {/* Target dates — SSOT display with inline Edit toggle.  The
       * UkDateInput remains the canonical edit control (keeps the
       * dd/mm/yyyy mask + ISO wire format contract locked by P2). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="mos-baseline-targets">
        <BaselineField
          testid="mos-baseline-target-exchange"
          label="Target Exchange" icon={<Calendar size={12} strokeWidth={2.4} />}
        >
          {(form.target_exchange && !isEditing("target_exchange")) ? (
            <div
              className="flex items-center justify-between gap-2 w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px]"
              data-testid="mos-baseline-target-exchange-confirmed"
            >
              <span className="font-semibold text-slate-800">{isoToUk(form.target_exchange) || form.target_exchange}</span>
              <button
                type="button"
                onClick={() => openEditFor("target_exchange")}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
                data-testid="mos-baseline-target-exchange-edit"
              >
                <Pencil size={10} strokeWidth={2.4} /> Edit
              </button>
            </div>
          ) : (
            <UkDateInput
              value={form.target_exchange || ""}
              onChange={(iso) => onField("target_exchange", iso)}
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="mos-baseline-target-exchange-input"
              ariaLabel="Target exchange date · dd/mm/yyyy"
            />
          )}
        </BaselineField>
        <BaselineField
          testid="mos-baseline-target-completion"
          label="Target Completion" icon={<Calendar size={12} strokeWidth={2.4} />}
        >
          {(form.target_completion && !isEditing("target_completion")) ? (
            <div
              className="flex items-center justify-between gap-2 w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px]"
              data-testid="mos-baseline-target-completion-confirmed"
            >
              <span className="font-semibold text-slate-800">{isoToUk(form.target_completion) || form.target_completion}</span>
              <button
                type="button"
                onClick={() => openEditFor("target_completion")}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
                data-testid="mos-baseline-target-completion-edit"
              >
                <Pencil size={10} strokeWidth={2.4} /> Edit
              </button>
            </div>
          ) : (
            <UkDateInput
              value={form.target_completion || ""}
              onChange={(iso) => onField("target_completion", iso)}
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="mos-baseline-target-completion-input"
              ariaLabel="Target completion date · dd/mm/yyyy"
            />
          )}
        </BaselineField>
      </div>

      {/* Additional details — Chain Position + Lender + Special
       * Conditions are all optional.  Collapsed by default so the
       * verification pane stays scannable; lender collapses to
       * "N/A · Cash purchase" when Funding Basis is Cash. */}
      <div data-testid="mos-baseline-extras">
        <button
          type="button"
          onClick={() => setShowAdditional((v) => !v)}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-600 hover:text-slate-800"
          data-testid="mos-baseline-additional-toggle"
          aria-expanded={showAdditional}
        >
          {showAdditional ? <ChevronUp size={12} strokeWidth={2.4} /> : <ChevronDown size={12} strokeWidth={2.4} />}
          Additional details (optional)
        </button>
        {showAdditional && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="mos-baseline-additional-fields">
            <BaselineField
              testid="mos-baseline-chain-position"
              label="Chain Position"
            >
              <select
                value={form.chain_position || ""}
                onChange={(e) => onField("chain_position", e.target.value)}
                className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                data-testid="mos-baseline-chain-position-input"
              >
                <option value="">Not confirmed</option>
                {CHAIN_POSITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </BaselineField>
            <BaselineField
              testid="mos-baseline-lender"
              label="Lender (optional)" icon={<Landmark size={12} strokeWidth={2.4} />}
            >
              {isCash ? (
                <div
                  className="w-full rounded-md ring-1 ring-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12.5px] text-slate-500 italic"
                  data-testid="mos-baseline-lender-na"
                >
                  N/A · Cash purchase
                </div>
              ) : (
                <input
                  type="text"
                  value={form.lender || ""}
                  onChange={(e) => onField("lender", e.target.value)}
                  placeholder="e.g. Halifax"
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mos-baseline-lender-input"
                />
              )}
            </BaselineField>
            <BaselineField
              testid="mos-baseline-special-conditions"
              label="Special Conditions (optional)"
            >
              <input
                type="text"
                value={form.special_conditions || ""}
                onChange={(e) => onField("special_conditions", e.target.value)}
                placeholder="Gifted deposit · HTB · retentions…"
                className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                data-testid="mos-baseline-special-conditions-input"
              />
            </BaselineField>
          </div>
        )}
      </div>

      {/* Missing summary — only when there's work to do */}
      {!isVerified && (
        <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 flex items-start gap-2" data-testid="mos-baseline-missing-banner">
          <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0" strokeWidth={2.4} />
          <p className="text-[12px] text-amber-800">
            <span className="font-semibold">{missingRequired.length}</span> detail{missingRequired.length === 1 ? "" : "s"} needed before issuing: <span className="font-semibold">{missingRequired.map((k) => FIELD_LABEL[k] || k).join(", ")}</span>.
          </p>
        </div>
      )}
    </div>
  );
}


function BaselineField({ label, required, missing, icon, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">
        {icon}
        {label}
        {required ? (
          <span
            className={`ml-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold tracking-normal normal-case ${missing ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
            data-testid={`${testid}-chip`}
          >
            {missing ? "Needs confirmation" : "Confirmed"}
          </span>
        ) : null}
      </span>
      {children}
    </label>
  );
}


function BaselineContactField({
  label, required, missing, icon, testid,
  value, options, onChange,
  contact, editing, onOpenEdit, cashNa,
}) {
  /* iter-alignment · P0 · MoS · Transaction Baseline UX Cleanup
   * (2026-02) — Three render modes, in priority order:
   *   1. `cashNa` → the mortgage broker row when funding basis
   *       is Cash.  Displayed as calm N/A chip; the participant
   *       is NOT counted as missing (the parent's brokerRequired
   *       derivation already excludes cash).
   *   2. `contact && !editing` → confirmed summary + Change
   *       action; keeps the pane in verification mode.
   *   3. Fallback → inline `<select>` picker.  This is the same
   *       canonical participant picker every other MoS build has
   *       used — no duplicate state, no new modal.
   */
  if (cashNa) {
    return (
      <BaselineField label={label} icon={icon} testid={testid}>
        <div
          className="w-full rounded-md ring-1 ring-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12.5px] text-slate-500 italic"
          data-testid={`${testid}-na`}
        >
          N/A · Cash purchase
        </div>
      </BaselineField>
    );
  }
  if (contact && !editing) {
    return (
      <BaselineField label={label} required={required} missing={false} icon={icon} testid={testid}>
        <div
          className="flex items-center justify-between gap-2 w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px]"
          data-testid={`${testid}-confirmed`}
        >
          <span className="min-w-0 truncate">
            <span className="font-semibold text-slate-800">{contact.name}</span>
            {contact.company ? <span className="text-slate-500"> · {contact.company}</span> : null}
          </span>
          <button
            type="button"
            onClick={onOpenEdit}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900 shrink-0"
            data-testid={`${testid}-change`}
          >
            <Pencil size={10} strokeWidth={2.4} /> Change
          </button>
        </div>
      </BaselineField>
    );
  }
  return (
    <BaselineField label={label} required={required} missing={missing} icon={icon} testid={testid}>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
        data-testid={`${testid}-select`}
      >
        <option value="">Not selected</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>
        ))}
      </select>
    </BaselineField>
  );
}
